import React, { useEffect, useState } from "react";
import apiClient from "../utils/apiClient";
import { useNavigate } from "react-router-dom";

import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

import {
  FaUsers,
  FaLaptop,
  FaCheckSquare,
  FaUndo,
  FaExchangeAlt,
  FaBox,
} from "react-icons/fa";

import "./AssetDashboard.css";


const COLORS = [
  "#4f46e5",
  "#06b6d4",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
];

function AssetDashboard() {
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const data = await apiClient(
        "/assets/list/"
      );

      const assetList = Array.isArray(data)
        ? data
        : data?.results || data?.assets || [];

      setAssignments(assetList);
    } catch (error) {
      console.error("Error fetching assets:", error);
    }
  };

  /* ======================
      Dashboard Counts
  ======================= */

  const employeeCount = new Set(
    assignments.map((a) => a.employee_id)
  ).size;

  const totalRecords = assignments.length;
  const navigate = useNavigate();

  const activeCount = assignments.filter(
    (a) => a.status === "Active"
  ).length;

  const returnedCount = assignments.filter(
    (a) => a.status === "Returned"
  ).length;

  const reassignedCount = assignments.filter(
    (a) => a.status === "Reassigned"
  ).length;

  const poolCount = assignments.filter(
    (a) => !a.employee_id
  ).length;

  const stats = [
    // {
    //   title: "Employees",
    //   value: employeeCount,
    //   icon: <FaUsers />,
    // },
    {
      title: "Total Records",
      value: totalRecords,
      icon: <FaLaptop />,
    },
    {
      title: "Active",
      value: activeCount,
      icon: <FaCheckSquare />,
    },
    {
      title: "Returned",
      value: returnedCount,
      icon: <FaUndo />,
    },
    {
      title: "Reassigned",
      value: reassignedCount,
      icon: <FaExchangeAlt />,
    },
    {
      title: "Pool",
      value: poolCount,
      icon: <FaBox />,
    },
  ];

  /* ======================
      Department Chart
  ======================= */

  const departmentData = Object.values(
    assignments.reduce((acc, item) => {
      const dept = item.department || "Unknown";

      if (!acc[dept]) {
        acc[dept] = {
          name: dept,
          value: 0,
        };
      }

      acc[dept].value++;

      return acc;
    }, {})
  );

  /* ======================
      Brand Chart
  ======================= */

  const brandData = Object.values(
    assignments.reduce((acc, item) => {
      const brand = item.brand || "Unknown";

      if (!acc[brand]) {
        acc[brand] = {
          name: brand,
          value: 0,
        };
      }

      acc[brand].value++;

      return acc;
    }, {})
  );

  /* ======================
      Monthly Trend
  ======================= */

  const trendData = Object.values(
    assignments.reduce((acc, item) => {
      if (!item.assigned_date) return acc;

      const month = new Date(
        item.assigned_date
      ).toLocaleString("default", {
        month: "short",
        year: "2-digit",
      });

      if (!acc[month]) {
        acc[month] = {
          month,
          count: 0,
        };
      }

      acc[month].count++;

      return acc;
    }, {})
  );

  /* ======================
      Recent Activity
  ======================= */

  const recentActivity = [...assignments]
    .sort(
      (a, b) =>
        new Date(b.assigned_date) -
        new Date(a.assigned_date)
    )
    .slice(0, 10);

  return (
  <div className="assetdash-container">

    <div className="assetdash-hero">
      <div>
        <h1>Asset Management Dashboard</h1>
      </div>

      <div className="assetdash-badge">
        <FaLaptop />
        <button
  className="assetdash-btn"
  onClick={() =>
    navigate("/dashboard/ManagerAcess/assetassignments")
  }
>
  <span>{totalRecords} Assets</span>
</button>
      </div>
    </div>

    <div className="assetdash-stats-grid">
      {stats.map((item, index) => (
        <div className="assetdash-stat-card" key={index}>
          <div>
            <span>{item.title}</span>
            <h3>{item.value}</h3>
          </div>

          <div className="assetdash-icon">
            {item.icon}
          </div>
        </div>
      ))}
    </div>

    <div className="assetdash-chart-grid">

      <div className="assetdash-chart-card">
        <h3>Assignments by Department</h3>

        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={departmentData}>
            <XAxis dataKey="name" />
            <Tooltip />
            <Bar dataKey="value" fill="#6366f1" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="assetdash-chart-card">
        <h3>Brand Distribution</h3>

        <ResponsiveContainer width="100%" height={300}>
          <PieChart>

            <Pie
              data={brandData}
              dataKey="value"
              nameKey="name"
              outerRadius={90}
              label={({ name, value }) =>
                `${name} (${value})`
              }
            >
              {brandData.map((entry, index) => (
                <Cell
                  key={index}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>

            <Tooltip
              formatter={(value, name) => [
                value,
                `${name} Laptops`,
              ]}
            />

            {/* <Legend
              formatter={(value, entry) =>
                `${value} (${entry.payload.value})`
              }
            /> */}

          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="assetdash-chart-card">
        <h3>Monthly Assignment Trend</h3>

        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={trendData}>
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />

            <Line
              type="monotone"
              dataKey="count"
              stroke="#0ea5e9"
              strokeWidth={3}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

    </div>

    <div className="assetdash-table-card">

      <div className="assetdash-table-header">
        <h3>Recent Activity</h3>
      </div>

      <table className="assetdash-table">
        <thead>
          <tr>
            <th>Employee</th>
            <th>Asset No</th>
            <th>Brand / Model</th>
            <th>Assigned Date</th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          {recentActivity.length > 0 ? (
            recentActivity.map((item) => (
              <tr key={item.id}>
                <td>{item.employee_name}</td>
                <td>{item.asset_number}</td>
                <td>{item.brand} {item.model}</td>
                <td>{item.assigned_date}</td>

                <td>
                  <span
                    className={`assetdash-status assetdash-${item.status?.toLowerCase()}`}
                  >
                    {item.status}
                  </span>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5">
                No Asset Records Found
              </td>
            </tr>
          )}
        </tbody>
      </table>

    </div>

  </div>
);
}

export default AssetDashboard;